import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogoFull } from '../components/Logo';
import { useAuth } from '../lib/auth';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { isValidRut, formatRut, formatRutInput } from '../lib/rut';
import api from '../lib/api';

export default function Register() {
  const [form, setForm] = useState({
    nombre: '', apellido: '', email: '', password: '',
    razon_social: '', rut: '', giro: '', website: '',
    telefono: '', cargo: '',
  });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);
  const [rutError, setRutError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingDiagId = searchParams.get('diag');

  // Precarga datos si venimos de un diagnóstico anónimo con contact info
  useEffect(() => {
    if (!pendingDiagId) return;
    (async () => {
      try {
        const { data } = await api.get(`/public/diagnostic/${pendingDiagId}`);
        const c = data?.contact || {};
        setForm((f) => ({
          ...f,
          email: c.email || f.email,
          nombre: c.nombre || f.nombre,
          razon_social: c.razon_social || f.razon_social,
          rut: c.rut || f.rut,
          telefono: c.telefono || f.telefono,
        }));
      } catch { /* silencioso */ }
    })();
  }, [pendingDiagId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateRut = () => {
    if (!form.rut) { setRutError(null); return false; }
    if (!isValidRut(form.rut)) {
      setRutError('RUT inválido. Verifique el número y el dígito verificador.');
      return false;
    }
    setRutError(null);
    setForm((f) => ({ ...f, rut: formatRut(f.rut) }));
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!accepted) {
      setError('Debe aceptar los Términos y la Política de Privacidad para continuar.');
      return;
    }
    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (!isValidRut(form.rut)) {
      setRutError('RUT inválido. Verifique el número y el dígito verificador.');
      return;
    }
    setSubmitting(true);
    try {
      await register(form);
      if (pendingDiagId) {
        try {
          await api.post(`/public/diagnostic/${pendingDiagId}/claim`);
          toast.success('Organización creada y diagnóstico guardado en su cuenta.');
        } catch {
          toast.success('Organización creada.');
        }
      } else {
        toast.success('Organización creada. Comencemos.');
      }
      navigate('/onboarding');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No pudimos completar el registro.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <LogoFull height={30} />
          <Link to="/login" className="text-sm text-[#374151] hover:text-[#1C2329] flex items-center gap-1" data-testid="link-login">
            <ArrowLeft size={14} /> Ya tengo cuenta
          </Link>
        </div>

        <div className="pv-card p-8 md:p-10">
          <div className="pv-eyebrow mb-2">Registro</div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#1C2329] tracking-tight">
            Cree su cuenta de organización
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Su empresa tendrá su propio espacio aislado. Luego lo llevamos al onboarding y al diagnóstico inicial.
          </p>

          <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="register-form">
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Nombre</label>
              <input required className="pv-input" value={form.nombre} onChange={set('nombre')} data-testid="reg-nombre" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Apellido</label>
              <input required className="pv-input" value={form.apellido} onChange={set('apellido')} data-testid="reg-apellido" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Correo corporativo</label>
              <input required type="email" className="pv-input" value={form.email} onChange={set('email')} data-testid="reg-email" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Contraseña</label>
              <input required type="password" minLength={8} className="pv-input" value={form.password} onChange={set('password')} data-testid="reg-password" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Razón social</label>
              <input required className="pv-input" value={form.razon_social} onChange={set('razon_social')} data-testid="reg-razon-social" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">RUT empresa</label>
              <input
                required
                className={`pv-input ${rutError ? 'border-[#DC2626] focus:border-[#DC2626]' : ''}`}
                value={form.rut}
                onChange={(e) => { setRutError(null); setForm((f) => ({ ...f, rut: formatRutInput(e.target.value) })); }}
                onBlur={validateRut}
                placeholder="12.345.678-9"
                data-testid="reg-rut"
              />
              {rutError && (
                <div className="text-[12px] text-[#991B1B] mt-1" data-testid="reg-rut-error">{rutError}</div>
              )}
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Giro / actividad</label>
              <input required className="pv-input" value={form.giro} onChange={set('giro')} placeholder="Servicios de tecnología" data-testid="reg-giro" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Sitio web (opcional)</label>
              <input className="pv-input" value={form.website} onChange={set('website')} placeholder="https://…" data-testid="reg-website" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Cargo</label>
              <input className="pv-input" value={form.cargo} onChange={set('cargo')} placeholder="Gerente de Operaciones" data-testid="reg-cargo" />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Teléfono (opcional)</label>
              <input className="pv-input" value={form.telefono} onChange={set('telefono')} placeholder="+56 9 …" data-testid="reg-telefono" />
            </div>

            {error && (
              <div className="md:col-span-2 text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FCA5A5]/60 px-3 py-2 rounded-md" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="md:col-span-2 pt-2">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="reg-accept-terms-label">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 border-2 border-[#D1D5DB] rounded-sm accent-[#143A34] cursor-pointer shrink-0"
                  data-testid="reg-accept-terms"
                />
                <span className="text-[13px] text-[#374151] leading-snug">
                  He leído y acepto los{' '}
                  <Link to="/terminos" className="text-[#143A34] underline hover:no-underline" target="_blank" rel="noreferrer">Términos y Condiciones</Link>
                  {' '}y la{' '}
                  <Link to="/privacidad" className="text-[#143A34] underline hover:no-underline" target="_blank" rel="noreferrer">Política de Privacidad</Link>
                  {' '}de Priviax.
                </span>
              </label>
            </div>

            <div className="md:col-span-2 flex justify-end pt-2">
              <button
                type="submit"
                disabled={submitting || !accepted}
                className="pv-btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="register-submit"
              >
                {submitting ? 'Creando cuenta…' : 'Crear cuenta'}
                {!submitting && <ArrowRight size={16} strokeWidth={1.75} />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
